// SettingsTab — Phase 5 Task 15 port of docs/mockups/leaderboard-detail.html
// Settings state (L3589→3997). Hosts the edit-mode LeaderboardConfigForm plus a
// Danger-zone card with Pause + Archive actions.
//
// The form is reused verbatim from LeaderboardConfigForm; on submit we strip
// the immutable fields (ext_key, operator, sort, window_kind, scope,
// guild_aggregator, segmentation, bucket_size, tier_mode) so the PATCH body
// matches UpdateLeaderboardInput exactly — the backend rejects any of those
// keys post-create.
//
// Pause is reversible and applies immediately. Archive is permanent and gated
// behind a type-to-confirm modal that asks the operator to retype the
// leaderboard's ext_key before the destructive call is dispatched.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Button } from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import { ApiError, describeError } from "../../../lib/errors";
import { LeaderboardConfigForm } from "../LeaderboardConfigForm";
import {
  archiveLeaderboard,
  lbKeys,
  updateLeaderboard,
} from "../api";
import type {
  CreateLeaderboardInput,
  Leaderboard,
  UpdateLeaderboardInput,
} from "../types";
import styles from "./SettingsTab.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────────────────────────

export interface SettingsTabProps {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  onArchived: () => void;
}

// ──────────────────────────────────────────────────────────────────────────────
// Field-projection helpers
// ──────────────────────────────────────────────────────────────────────────────

/** Map a Leaderboard row into a CreateLeaderboardInput shape for the form's
 *  `initial` prop. Straight copy of fields that exist on both DTOs. */
function leaderboardToFormInitial(lb: Leaderboard): Partial<CreateLeaderboardInput> {
  return {
    ext_key: lb.ext_key,
    name: lb.name,
    description: lb.description,
    scope: lb.scope,
    guild_aggregator: lb.guild_aggregator,
    operator: lb.operator,
    sort: lb.sort,
    window_kind: lb.window_kind,
    cron: lb.cron,
    event_start_at: lb.event_start_at,
    event_end_at: lb.event_end_at,
    segmentation: lb.segmentation,
    bucket_size: lb.bucket_size,
    tier_mode: lb.tier_mode,
    tier_config: lb.tier_config,
    realtime: lb.realtime,
    submit_policy: lb.submit_policy,
    guards: lb.guards,
    visibility: lb.visibility,
    metadata: lb.metadata,
  };
}

/**
 * Project the form's CreateLeaderboardInput payload down to the subset the
 * PATCH endpoint accepts. Anything outside UpdateLeaderboardInput's union
 * (ext_key, operator, sort, window_kind, scope, guild_aggregator,
 * segmentation, bucket_size, tier_mode) is dropped on the floor — the
 * backend's UpdateParams rejects them.
 */
function buildUpdatePayload(form: CreateLeaderboardInput): UpdateLeaderboardInput {
  const body: UpdateLeaderboardInput = {
    name: form.name,
    description: form.description,
    realtime: form.realtime,
    submit_policy: form.submit_policy,
    guards: form.guards,
    visibility: form.visibility,
    metadata: form.metadata,
  };
  // Conditional fields — only forward what's relevant for the configured
  // window. Always include null-erasure semantics for cron/event_*: passing
  // undefined leaves the value untouched; passing null clears it.
  if (form.window_kind === "recurring") {
    body.cron = form.cron ?? null;
  }
  if (form.window_kind === "event") {
    body.event_start_at = form.event_start_at ?? null;
    body.event_end_at = form.event_end_at ?? null;
  }
  if (form.tier_config !== undefined) {
    body.tier_config = form.tier_config;
  }
  return body;
}

function describeApiError(err: unknown): string {
  if (err instanceof ApiError) return describeError(err.code);
  return describeError("internal");
}

// ──────────────────────────────────────────────────────────────────────────────
// SettingsTab — entry point
// ──────────────────────────────────────────────────────────────────────────────

export function SettingsTab({ gameId, lbId, lb, onArchived }: SettingsTabProps) {
  const qc = useQueryClient();

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (body: UpdateLeaderboardInput) =>
      updateLeaderboard(gameId, lbId, body),
    onSuccess: () => {
      setSaveError(null);
      setSaveOk(true);
      qc.invalidateQueries({ queryKey: lbKeys.detail(gameId, lbId) });
      // Prefix-key invalidation across all list filters.
      qc.invalidateQueries({ queryKey: ["leaderboards", gameId] });
    },
    onError: (err) => {
      setSaveOk(false);
      setSaveError(describeApiError(err));
    },
  });

  const pauseMutation = useMutation({
    mutationFn: () => {
      const next: Leaderboard["status"] =
        lb.status === "paused" ? "active" : "paused";
      return updateLeaderboard(gameId, lbId, { status: next });
    },
    onSuccess: () => {
      setSaveError(null);
      qc.invalidateQueries({ queryKey: lbKeys.detail(gameId, lbId) });
      qc.invalidateQueries({ queryKey: ["leaderboards", gameId] });
    },
    onError: (err) => {
      setSaveError(describeApiError(err));
    },
  });

  function handleSubmit(input: CreateLeaderboardInput) {
    setSaveOk(false);
    setSaveError(null);
    saveMutation.mutate(buildUpdatePayload(input));
  }

  return (
    <div className={styles.wrap}>
      {saveOk && (
        <div className={styles.alertSlot}>
          <Alert variant="success" title="Saved">
            Configuration updated successfully.
          </Alert>
        </div>
      )}
      {saveError && (
        <div className={styles.alertSlot}>
          <Alert variant="danger" title="Couldn't save changes">
            {saveError}
          </Alert>
        </div>
      )}

      <LeaderboardConfigForm
        mode="edit"
        initial={leaderboardToFormInitial(lb)}
        saving={saveMutation.isPending}
        submitLabel="Save changes"
        onSubmit={handleSubmit}
        onCancel={() => {
          /* Reset is handled by remounting the page; nothing to do here. The
             ConfigForm owns its local state, so a Cancel click on the edit form
             is a no-op until the operator navigates away. */
        }}
      />

      <DangerZone
        lb={lb}
        onPauseToggle={() => pauseMutation.mutate()}
        pausePending={pauseMutation.isPending}
        onArchiveClick={() => setConfirmOpen(true)}
      />

      {confirmOpen && (
        <ArchiveConfirmModal
          gameId={gameId}
          lbId={lbId}
          lb={lb}
          onClose={() => setConfirmOpen(false)}
          onArchived={() => {
            setConfirmOpen(false);
            onArchived();
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Danger zone card
// ──────────────────────────────────────────────────────────────────────────────

function DangerZone({
  lb,
  onPauseToggle,
  pausePending,
  onArchiveClick,
}: {
  lb: Leaderboard;
  onPauseToggle: () => void;
  pausePending: boolean;
  onArchiveClick: () => void;
}) {
  if (lb.status === "archived") {
    return (
      <section className={styles.dangerZone} aria-label="Danger zone">
        <div className={styles.dzHead}>
          <AlertTriangle size={16} aria-hidden="true" />
          <h3>Danger zone</h3>
        </div>
        <div className={styles.dzMuted}>
          This leaderboard is already archived. Existing cycles remain readable.
        </div>
      </section>
    );
  }

  const isPaused = lb.status === "paused";
  const pauseLabel = isPaused ? "Resume leaderboard" : "Pause leaderboard";
  const pauseDesc = isPaused
    ? "Resume the leaderboard — start accepting new submits again."
    : "Pause the leaderboard — stop accepting new submits while keeping the current cycle visible.";

  return (
    <section className={styles.dangerZone} aria-label="Danger zone">
      <div className={styles.dzHead}>
        <AlertTriangle size={16} aria-hidden="true" />
        <h3>Danger zone</h3>
      </div>

      <div className={styles.dzRow}>
        <div className={styles.dzBody}>
          <h4>{isPaused ? "Resume leaderboard" : "Pause leaderboard"}</h4>
          <p>{pauseDesc}</p>
        </div>
        <Button
          variant={isPaused ? "primary" : "secondary"}
          onClick={onPauseToggle}
          loading={pausePending}
        >
          {pauseLabel}
        </Button>
      </div>

      <div className={styles.dzRow}>
        <div className={styles.dzBody}>
          <h4>Archive leaderboard</h4>
          <p>
            Archive the leaderboard — permanent. Existing cycles and scores
            remain readable, but no new cycles or submits will run.
          </p>
        </div>
        <Button variant="danger" onClick={onArchiveClick}>
          Archive leaderboard
        </Button>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Archive confirm modal (type-to-confirm)
// ──────────────────────────────────────────────────────────────────────────────

function ArchiveConfirmModal({
  gameId,
  lbId,
  lb,
  onClose,
  onArchived,
}: {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  onClose: () => void;
  onArchived: () => void;
}) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => archiveLeaderboard(gameId, lbId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lbKeys.detail(gameId, lbId) });
      qc.invalidateQueries({ queryKey: ["leaderboards", gameId] });
      onArchived();
    },
    onError: (err) => {
      setError(describeApiError(err));
    },
  });

  const canConfirm = typed === lb.ext_key && !mutation.isPending;
  const inputId = "archive-confirm-ext-key";

  return (
    <Modal
      open
      onClose={onClose}
      title="Archive leaderboard?"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={!canConfirm}
            loading={mutation.isPending}
          >
            Archive
          </Button>
        </>
      }
    >
      <p className={styles.modalBodyText}>
        This will archive <b>{lb.name}</b> (<code>{lb.ext_key}</code>).
        Existing cycles and scores remain readable, but no new cycles or
        submits will run. This action is permanent.
      </p>

      <label className={styles.modalLabel} htmlFor={inputId}>
        Type the ext_key to confirm
      </label>
      <input
        id={inputId}
        className={styles.input}
        autoComplete="off"
        spellCheck={false}
        placeholder={lb.ext_key}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
      />
      <span className={styles.helper}>
        Type <code>{lb.ext_key}</code> exactly to enable the Archive button.
      </span>

      {error && (
        <div className={styles.errorBlurb} role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
